import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { UUID } from "crypto";
import api from "@/app/api";
import { usePartners as usePartnersV2 } from "@stockline/web";
import type { Partner } from "@/app/api/types/partner";
import { partnerKeys } from "../keys/partners";

/**
 * Query keys for the legacy `/user-partners` write path. The read side is
 * gone — `is_user_partner` now ships on the V2 partner sync row itself, so
 * `useUserPartnerIds` derives the set client-side from offline-first Dexie
 * partners. We keep the key namespace because the toggle mutation still
 * invalidates it for any stragglers; it's effectively a no-op once nothing
 * else reads it.
 */
export const userPartnerKeys = {
  all: ["user-partners"] as const,
  list: () => [...userPartnerKeys.all, "list"] as const,
  check: (partnerId: UUID) =>
    [...userPartnerKeys.all, "check", partnerId] as const,
};

/**
 * Set of partner IDs the current viewer has favorited. Backed by the V2
 * offline-first partner cache: each Partner row carries `isUserPartner`
 * computed JOIN-side at sync time, so reading the set is a pure derivation
 * and works offline. No REST call.
 *
 * Recap consumers (`RecapHeader`, `RecapShell`, `usePartnerEditHandlers`,
 * `useGoToPreOrder`) use this Set with `.has(partnerId)`; keeping that
 * contract intact lets the call sites stay unchanged.
 */
export const useUserPartnerIds = (): Set<string> => {
  const partners = usePartnersV2();
  return useMemo(() => {
    const set = new Set<string>();
    for (const p of partners) {
      if (p.isUserPartner) set.add(p.id as string);
    }
    return set;
  }, [partners]);
};

/**
 * Toggle the current viewer's "favorite" relation on a partner. Still
 * writes through the legacy REST endpoint — V2 push is read-only for this
 * field. The next V2 partner sync re-evaluates the JOIN and lands the new
 * value on the Dexie row, so consumers see the flip naturally; the
 * optimistic block below patches the partner cache for the very next paint
 * to avoid a one-tick flicker before the sync round-trip lands.
 */
export function useToggleUserPartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (partnerId: string) =>
      api.userPartners.toggleUserPartner(
        partnerId as `${string}-${string}-${string}-${string}-${string}`,
      ),

    onMutate: (partnerId) => {
      // Optimistic update on any partner queries that legacy code paths still
      // hold in TanStack cache. Dexie/V2 will be reconciled on next sync, so
      // this is purely a transient first-paint nicety.
      const queries = queryClient.getQueryCache().findAll({
        queryKey: partnerKeys.all,
      });

      queries.forEach((query) => {
        const old = query.state.data as any;
        if (!old) return;

        if (Array.isArray(old.items)) {
          const newItems = old.items.map((p: Partner) => {
            if (p.id.toString() === partnerId) {
              return { ...p, is_user_partner: !p.is_user_partner };
            }
            return p;
          });
          queryClient.setQueryData(query.queryKey, {
            ...old,
            items: newItems,
          });
        } else if (Array.isArray(old.pages)) {
          const newPages = old.pages.map((page: any) => {
            if (!Array.isArray(page.items)) return page;
            return {
              ...page,
              items: page.items.map((p: Partner) => {
                if (p.id.toString() === partnerId) {
                  return { ...p, is_user_partner: !p.is_user_partner };
                }
                return p;
              }),
            };
          });
          queryClient.setQueryData(query.queryKey, {
            ...old,
            pages: newPages,
          });
        } else if (Array.isArray(old)) {
          queryClient.setQueryData(
            query.queryKey,
            old.map((p: Partner) => {
              if (p.id?.toString() === partnerId) {
                return { ...p, is_user_partner: !p.is_user_partner };
              }
              return p;
            }),
          );
        } else if (old.id?.toString() === partnerId) {
          queryClient.setQueryData(query.queryKey, {
            ...old,
            is_user_partner: !old.is_user_partner,
          });
        }
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userPartnerKeys.all });
    },
  });
}
